import { Field, useFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import { FieldProps } from '../../types/fields';
import { config } from '../../utils/config';
import { Country } from '../../utils/countryData';
import getValidator from '../../utils/getValidator';
import { getStreetHouseNumberFormat } from '../../utils/streetHouseNumber';
import TextField from '../formik/TextField';

type StreetHouseNumberFieldProps = Omit<FieldProps, 'context'> & { context: 'shippingAddress' | 'billingAddress' };

const StreetHouseNumberField = (props: StreetHouseNumberFieldProps) => {
  const { formatMessage } = useIntl();
  const { values } = useFormikContext<any>();

  const selectedCountry: Country = values[props.context].country;
  const { streetHouseNumber: streetHouseNumberConfig } = config.addressForm[selectedCountry];

  const houseNumberField = (
    <div className="form__field-container">
      <Field
        name={`${props.context}.houseNumber`}
        component={TextField}
        label={formatMessage({ id: 'address.houseNumber' })}
        maxLength={streetHouseNumberConfig.houseNumber.maxLength ?? 10}
        validate={getValidator({
          required: true,
          minLength: 1,
          format: /^\d+[-/]?(\d+)?([a-z]+)?/i,
          ...streetHouseNumberConfig.houseNumber,
        })}
        data-test-id={`${props.testIdPrefix}-houseNumber`}
      />
    </div>
  );

  return (
    <>
      {streetHouseNumberConfig.separateFields && (
        <>
          {streetHouseNumberConfig.houseNumber.position === 'first' && houseNumberField}
          <div className="form__field-container" style={{ flex: 4 }}>
            <Field
              name={`${props.context}.street`}
              component={TextField}
              label={formatMessage({ id: 'address.street' })}
              maxLength={streetHouseNumberConfig.street?.maxLength ?? 50}
              validate={getValidator({
                required: true,
                minLength: 3,
                format: 'streetOrCity',
                ...streetHouseNumberConfig.street,
              })}
              data-test-id={`${props.testIdPrefix}-street`}
            />
          </div>
          {streetHouseNumberConfig.houseNumber.position === 'second' && houseNumberField}
        </>
      )}
      {!streetHouseNumberConfig.separateFields && (
        <div className="form__field-container">
          <Field
            name={`${props.context}.streetHouseNumber`}
            component={TextField}
            label={formatMessage({ id: 'address.streetHouseNumber' })}
            maxLength={streetHouseNumberConfig.maxLength ?? 60}
            validate={getValidator({
              required: true,
              minLength: 5,
              format: getStreetHouseNumberFormat(selectedCountry),
              ...streetHouseNumberConfig,
            })}
            data-test-id={`${props.testIdPrefix}-streetHouseNumber`}
          />
        </div>
      )}
    </>
  );
};

export default StreetHouseNumberField;
